import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Avatar, AvatarFallback } from './ui/avatar';
import { 
  User, Edit2, Save, X, Heart, Activity, Droplet, Ruler, Scale, Dumbbell, Briefcase,
  AlertTriangle, Trash2, Loader2
} from 'lucide-react';
import { profileAPI } from '../services/api';
import { toast } from '../hooks/use-toast';

const ProfileTab = ({ user, onUserUpdate }) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [editData, setEditData] = useState({
    name: user.name || '', phone: user.phone || '', address: user.address || '',
    gender: user.gender || '', date_of_birth: user.date_of_birth || '', blood_type: user.blood_type || '',
    height: user.height || '', weight: user.weight || '', chronic_diseases: user.chronic_diseases || '',
    current_medications: user.current_medications || '', allergies: user.allergies || '',
    past_surgeries: user.past_surgeries || '', family_history: user.family_history || '',
    lifestyle: user.lifestyle || '', occupation: user.occupation || '', emergency_contact: user.emergency_contact || '',
    smoking_status: user.smoking_status || '', alcohol_consumption: user.alcohol_consumption || '',
    physical_activity: user.physical_activity || '', diet_type: user.diet_type || '',
    sleep_hours: user.sleep_hours || '', stress_level: user.stress_level || '',
    vaccination_status: user.vaccination_status || '', last_checkup_date: user.last_checkup_date || '',
  });
  const [saving, setSaving] = useState(false);

  const handleEdit = () => setIsEditing(true);
  const handleCancel = () => { setIsEditing(false); setEditData({...user}); };

  const handleDeleteProfile = async () => {
    if (deleteConfirmText !== 'УДАЛИТЬ') {
      toast({ title: 'Ошибка', description: 'Введите "УДАЛИТЬ" для подтверждения', variant: 'destructive' });
      return;
    }

    setDeleting(true);
    try {
      await profileAPI.deleteProfile();
      toast({ title: 'Профиль удален', description: 'Все данные успешно удалены' });
      localStorage.removeItem('token');
      localStorage.removeItem('isAuthenticated');
      navigate('/auth');
    } catch (error) {
      console.error('Error deleting profile:', error);
      toast({ title: 'Ошибка', description: 'Не удалось удалить профиль', variant: 'destructive' });
      setDeleting(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const cleanedData = {};
      Object.keys(editData).forEach(key => {
        const value = editData[key];
        cleanedData[key] = value === '' || value === undefined ? null : (key === 'height' || key === 'weight') ? (value ? parseFloat(value) : null) : value;
      });
      const updated = await profileAPI.updateProfile(cleanedData);
      onUserUpdate(updated);
      setIsEditing(false);
      toast({ title: 'Профиль обновлён', description: 'Ваши данные успешно сохранены' });
    } catch (error) {
      toast({ title: 'Ошибка', description: 'Не удалось обновить профиль', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const getInitials = (name) => name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
  const calculateAge = (dob) => dob ? Math.floor((new Date() - new Date(dob)) / 31557600000) : null;
  const calculateBMI = () => {
    if (!user.height || !user.weight) return null;
    const bmi = user.weight / Math.pow(user.height / 100, 2);
    const category = bmi < 18.5 ? 'Недостаточный' : bmi < 25 ? 'Нормальный' : bmi < 30 ? 'Избыточный' : 'Ожирение';
    const color = bmi < 18.5 ? 'text-amber-400' : bmi < 25 ? 'text-emerald-400' : bmi < 30 ? 'text-orange-400' : 'text-red-400';
    return { value: bmi.toFixed(1), category, color };
  };

  // Liquid Glass Card Component
  const GlassCard = ({ icon: Icon, iconGradient, title, children }) => (
    <div className="liquid-glass-card p-5 sm:p-6">
      <div className="relative z-10 flex items-center gap-3 mb-4">
        <div className={`liquid-glass-icon w-9 h-9 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${iconGradient.includes('cyan') ? 'text-cyan-400' : iconGradient.includes('red') ? 'text-red-400' : iconGradient.includes('orange') ? 'text-orange-400' : 'text-violet-400'}`} />
        </div>
        <h3 className="text-white font-medium text-base">{title}</h3>
      </div>
      <div className="relative z-10">
        {children}
      </div>
    </div>
  );

  // Liquid Glass Metric Card
  const MetricCard = ({ icon: Icon, iconGradient, label, value, unit, isEditing, editComponent }) => (
    <div className="liquid-glass-card p-4">
      <div className="relative z-10 flex items-center justify-between mb-2">
        <div className={`liquid-glass-icon w-8 h-8 flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${iconGradient.includes('cyan') ? 'text-cyan-400' : iconGradient.includes('emerald') ? 'text-emerald-400' : iconGradient.includes('purple') ? 'text-purple-400' : 'text-red-400'}`} />
        </div>
        <span className="text-white/40 text-[10px] uppercase tracking-wider font-medium">{label}</span>
      </div>
      <div className="relative z-10">
        {isEditing ? editComponent : (
          <div className="text-2xl sm:text-3xl font-bold text-white">
            {value || '--'}
            {unit && <span className="text-white/40 text-sm ml-1 font-normal">{unit}</span>}
          </div>
        )}
      </div>
    </div>
  );

  const FieldDisplay = ({ label, value, placeholder = 'Не указано' }) => (
    <div className="flex justify-between items-center py-3 border-b border-white/[0.06] last:border-0">
      <span className="text-white/50 text-sm">{label}</span>
      <span className="text-white text-sm font-medium text-right ml-2">
        {value || <span className="text-white/30">{placeholder}</span>}
      </span>
    </div>
  );

  const InputField = ({ label, value, onChange, type = "text", placeholder = "" }) => (
    <div className="space-y-1.5">
      <label className="text-white/60 text-xs font-medium">{label}</label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="bg-white/[0.05] border-white/[0.1] text-white placeholder:text-white/30 rounded-full h-11 px-4 text-sm focus:border-cyan-500/50 focus:ring-cyan-500/20" />
    </div>
  );

  const SelectField = ({ label, value, onChange, options }) => (
    <div className="space-y-1.5">
      <label className="text-white/60 text-xs font-medium">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.05] border border-white/[0.1] text-white rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-cyan-500/50">
        {options.map(opt => <option key={opt.value} value={opt.value} className="bg-gray-900">{opt.label}</option>)}
      </select>
    </div>
  );

  const bmiData = calculateBMI();
  const age = calculateAge(user.date_of_birth);

  return (
    <div className="space-y-5">
      {/* Hero Profile Card - Liquid Glass */}
      <div className="liquid-glass-card p-5 sm:p-8">
        <div className="relative z-10 flex flex-col sm:flex-row items-center gap-5 sm:gap-6">
          <Avatar className="w-20 h-20 sm:w-24 sm:h-24 ring-4 ring-white/10 shadow-xl">
            <AvatarFallback className="bg-gradient-to-br from-cyan-500 to-blue-500 text-white text-xl sm:text-2xl font-bold">
              {getInitials(user.name)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 text-center sm:text-left">
            <h2 className="text-2xl sm:text-3xl font-bold text-white">{user.name}</h2>
            <p className="text-white/50 text-sm mt-1">{user.email}</p>
            {age && (
              <div className="flex items-center justify-center sm:justify-start gap-2 mt-3">
                <span className="px-3 py-1 bg-white/[0.08] backdrop-blur-sm rounded-full text-white/70 text-xs font-medium border border-white/10">{age} лет</span>
                {user.blood_type && <span className="px-3 py-1 bg-red-500/20 backdrop-blur-sm rounded-full text-red-300 text-xs font-medium border border-red-500/20">{user.blood_type}</span>}
              </div>
            )}
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            {!isEditing ? (
              <Button onClick={handleEdit} className="flex-1 sm:flex-initial bg-white/[0.08] hover:bg-white/[0.15] text-white border border-white/[0.15] rounded-full px-5 py-2.5 transition-all hover:scale-[1.02] backdrop-blur-sm">
                <Edit2 className="mr-2" size={16} />Редактировать
              </Button>
            ) : (
              <>
                <Button onClick={handleSave} disabled={saving} className="flex-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/30 rounded-full transition-all backdrop-blur-sm">
                  <Save className="mr-2" size={16} />{saving ? 'Сохранение...' : 'Сохранить'}
                </Button>
                <Button onClick={handleCancel} className="flex-1 bg-white/[0.05] hover:bg-white/[0.1] text-white/70 border border-white/[0.1] rounded-full backdrop-blur-sm">
                  <X className="mr-2" size={16} />Отмена
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <MetricCard icon={Ruler} iconGradient="bg-gradient-to-br from-cyan-500 to-blue-500" label="Рост" value={user.height} unit="см" isEditing={isEditing}
          editComponent={<Input type="number" value={editData.height} onChange={(e) => setEditData({...editData, height: e.target.value})} placeholder="170" className="bg-white/[0.05] border-white/[0.1] text-white text-xl font-bold h-11 rounded-full" />} />
        <MetricCard icon={Scale} iconGradient="bg-gradient-to-br from-emerald-500 to-green-500" label="Вес" value={user.weight} unit="кг" isEditing={isEditing}
          editComponent={<Input type="number" value={editData.weight} onChange={(e) => setEditData({...editData, weight: e.target.value})} placeholder="70" className="bg-white/[0.05] border-white/[0.1] text-white text-xl font-bold h-11 rounded-full" />} />
        <div className="liquid-glass-card p-4">
          <div className="relative z-10 flex items-center justify-between mb-2">
            <div className="liquid-glass-icon w-8 h-8 flex items-center justify-center">
              <Activity className="w-4 h-4 text-purple-400" />
            </div>
            <span className="text-white/40 text-[10px] uppercase tracking-wider font-medium">ИМТ</span>
          </div>
          <div className="relative z-10 text-2xl sm:text-3xl font-bold text-white">{bmiData?.value || '--'}</div>
          {bmiData && <p className={`relative z-10 text-xs mt-1 font-medium ${bmiData.color}`}>{bmiData.category}</p>}
        </div>
        <MetricCard icon={Droplet} iconGradient="bg-gradient-to-br from-red-500 to-rose-500" label="Группа крови" value={user.blood_type} isEditing={isEditing}
          editComponent={<select value={editData.blood_type} onChange={(e) => setEditData({...editData, blood_type: e.target.value})} className="w-full bg-white/[0.05] border border-white/[0.1] text-white rounded-full px-3 py-2 text-lg font-bold">
            <option value="" className="bg-gray-900">--</option>
            {['O+', 'O-', 'A+', 'A-', 'B+', 'B-', 'AB+', 'AB-'].map(t => <option key={t} value={t} className="bg-gray-900">{t}</option>)}
          </select>} />
      </div>

      {/* Info Cards Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <GlassCard icon={User} iconGradient="bg-gradient-to-br from-cyan-500 to-blue-500" title="Личные данные">
          {isEditing ? (
            <div className="space-y-3">
              <InputField label="Имя" value={editData.name} onChange={(v) => setEditData({...editData, name: v})} />
              <InputField label="Телефон" value={editData.phone} onChange={(v) => setEditData({...editData, phone: v})} placeholder="+7..." />
              <InputField label="Дата рождения" type="date" value={editData.date_of_birth} onChange={(v) => setEditData({...editData, date_of_birth: v})} />
              <SelectField label="Пол" value={editData.gender} onChange={(v) => setEditData({...editData, gender: v})} options={[{value: '', label: 'Не указан'}, {value: 'male', label: 'Мужской'}, {value: 'female', label: 'Женский'}]} />
            </div>
          ) : (
            <div><FieldDisplay label="Имя" value={user.name} /><FieldDisplay label="Email" value={user.email} /><FieldDisplay label="Телефон" value={user.phone} /><FieldDisplay label="Дата рождения" value={user.date_of_birth} /><FieldDisplay label="Пол" value={user.gender === 'male' ? 'Мужской' : user.gender === 'female' ? 'Женский' : null} /></div>
          )}
        </GlassCard>

        <GlassCard icon={Heart} iconGradient="bg-gradient-to-br from-red-500 to-pink-500" title="Медицинская информация">
          {isEditing ? (
            <div className="space-y-3">
              <div className="space-y-1.5"><label className="text-white/60 text-xs font-medium">Хронические заболевания</label>
                <textarea value={editData.chronic_diseases} onChange={(e) => setEditData({...editData, chronic_diseases: e.target.value})} placeholder="Диабет, гипертония..."
                  className="w-full bg-white/[0.05] border border-white/[0.1] text-white rounded-xl px-4 py-2.5 text-sm resize-none h-20 focus:outline-none focus:border-cyan-500/50" /></div>
              <div className="space-y-1.5"><label className="text-white/60 text-xs font-medium">Аллергии</label>
                <textarea value={editData.allergies} onChange={(e) => setEditData({...editData, allergies: e.target.value})} placeholder="Лекарства, продукты..."
                  className="w-full bg-white/[0.05] border border-white/[0.1] text-white rounded-xl px-4 py-2.5 text-sm resize-none h-20 focus:outline-none focus:border-cyan-500/50" /></div>
            </div>
          ) : (
            <div><FieldDisplay label="Хронические заболевания" value={user.chronic_diseases} /><FieldDisplay label="Аллергии" value={user.allergies} /><FieldDisplay label="Текущие лекарства" value={user.current_medications} /><FieldDisplay label="Последний осмотр" value={user.last_checkup_date} /></div>
          )}
        </GlassCard>

        <GlassCard icon={Dumbbell} iconGradient="bg-gradient-to-br from-orange-500 to-amber-500" title="Образ жизни">
          {isEditing ? (
            <div className="space-y-3">
              <SelectField label="Курение" value={editData.smoking_status} onChange={(v) => setEditData({...editData, smoking_status: v})} options={[{value: '', label: 'Не указано'}, {value: 'never', label: 'Никогда'}, {value: 'former', label: 'Бросил'}, {value: 'current', label: 'Курю'}]} />
              <SelectField label="Алкоголь" value={editData.alcohol_consumption} onChange={(v) => setEditData({...editData, alcohol_consumption: v})} options={[{value: '', label: 'Не указано'}, {value: 'never', label: 'Не употребляю'}, {value: 'rarely', label: 'Редко'}, {value: 'moderate', label: 'Умеренно'}]} />
              <SelectField label="Физическая активность" value={editData.physical_activity} onChange={(v) => setEditData({...editData, physical_activity: v})} options={[{value: '', label: 'Не указано'}, {value: 'sedentary', label: 'Малоподвижный'}, {value: 'light', label: 'Легкая'}, {value: 'moderate', label: 'Умеренная'}, {value: 'active', label: 'Активная'}]} />
            </div>
          ) : (
            <div><FieldDisplay label="Курение" value={user.smoking_status === 'never' ? 'Никогда' : user.smoking_status === 'former' ? 'Бросил' : user.smoking_status === 'current' ? 'Курю' : null} />
            <FieldDisplay label="Алкоголь" value={user.alcohol_consumption === 'never' ? 'Не употребляю' : user.alcohol_consumption === 'rarely' ? 'Редко' : user.alcohol_consumption === 'moderate' ? 'Умеренно' : null} />
            <FieldDisplay label="Физ. активность" value={user.physical_activity === 'sedentary' ? 'Малоподвижный' : user.physical_activity === 'light' ? 'Легкая' : user.physical_activity === 'moderate' ? 'Умеренная' : user.physical_activity === 'active' ? 'Активная' : null} />
            <FieldDisplay label="Сон" value={user.sleep_hours ? `${user.sleep_hours} ч.` : null} /></div>
          )}
        </GlassCard>

        <GlassCard icon={Briefcase} iconGradient="bg-gradient-to-br from-indigo-500 to-violet-500" title="Работа и контакты">
          {isEditing ? (
            <div className="space-y-3">
              <InputField label="Профессия" value={editData.occupation} onChange={(v) => setEditData({...editData, occupation: v})} />
              <InputField label="Экстренный контакт" value={editData.emergency_contact} onChange={(v) => setEditData({...editData, emergency_contact: v})} placeholder="Имя и телефон" />
            </div>
          ) : (
            <div><FieldDisplay label="Профессия" value={user.occupation} /><FieldDisplay label="Экстренный контакт" value={user.emergency_contact} /><FieldDisplay label="Семейный анамнез" value={user.family_history} /></div>
          )}
        </GlassCard>
      </div>

      {/* Delete Account Section - Liquid Glass */}
      <div className="liquid-glass-card p-5 sm:p-6" style={{ background: 'rgba(239, 68, 68, 0.08)', borderColor: 'rgba(239, 68, 68, 0.2)' }}>
        <div className="relative z-10 flex items-start gap-4 mb-4">
          <div className="liquid-glass-icon p-3 flex-shrink-0" style={{ background: 'rgba(239, 68, 68, 0.15)' }}>
            <AlertTriangle className="text-red-400" size={20} />
          </div>
          <div>
            <h3 className="text-red-400 text-base font-medium">Опасная зона</h3>
            <p className="text-white/50 text-sm mt-1">
              Удаление аккаунта приведет к безвозвратной потере всех данных
            </p>
          </div>
        </div>

        <div className="relative z-10">
          {!showDeleteConfirm ? (
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              className="bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 rounded-full h-10 px-5 backdrop-blur-sm transition-all hover:scale-[1.02]"
              data-testid="delete-account-btn"
            >
              <Trash2 className="mr-2" size={16} />
              Удалить аккаунт
            </Button>
          ) : (
            <div className="liquid-glass-card p-4 space-y-4" style={{ background: 'rgba(0, 0, 0, 0.3)' }}>
              <div className="relative z-10 space-y-2">
                <p className="text-white font-medium text-sm">Вы уверены?</p>
                <p className="text-white/50 text-xs">Это действие нельзя отменить. Будут удалены все ваши данные.</p>
              </div>

              <div className="relative z-10 space-y-2">
                <label className="text-white/60 text-xs">
                  Введите <span className="text-red-400 font-bold">УДАЛИТЬ</span>:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="УДАЛИТЬ"
                  className="w-full bg-white/5 border border-white/20 text-white rounded-full px-4 py-2.5 text-sm focus:outline-none focus:border-red-500/50 backdrop-blur-sm"
                  data-testid="delete-confirm-input"
                />
              </div>

              <div className="relative z-10 flex gap-3">
                <Button
                  onClick={handleDeleteProfile}
                  disabled={deleteConfirmText !== 'УДАЛИТЬ' || deleting}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white border-0 rounded-full h-10 disabled:opacity-50"
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="mr-2 animate-spin" size={16} />
                      Удаление...
                    </>
                  ) : (
                    <>
                      <Trash2 className="mr-2" size={16} />
                      Удалить навсегда
                    </>
                  )}
                </Button>
                <Button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                  disabled={deleting}
                  className="flex-1 bg-white/10 hover:bg-white/20 text-white border border-white/20 rounded-full h-10 backdrop-blur-sm"
                  data-testid="cancel-delete-btn"
                >
                  Отмена
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <p className="text-center text-white/30 text-xs">* Заполнение профиля поможет AI давать более точные рекомендации</p>
    </div>
  );
};

export default ProfileTab;
